// ── UpiPayPanel ─────────────────────────────────────────────────────────────────
// The one place a UPI payment is presented. Used by the group join fee (both the group page and the
// scanned-QR page) and by a shop listing, which previously each had their own near-identical copy.
//
// Ordered deliberately, from what always works to what sometimes does:
//
//   1. The QR, scanned from INSIDE a UPI app. This is the reliable route and the instruction matters:
//      scanning with the phone's camera hands the upi:// link back to whichever app claimed the
//      scheme — often WhatsApp — which is the failure this layout exists to avoid.
//      When the payee has uploaded their own QR out of their UPI app (`qrImage`), that image is shown
//      instead of one generated from the VPA: it is the payee's real collect code, so PSP apps accept
//      it without the person-to-person objections a derived upi:// intent runs into.
//   2. Tap the code to pay. A UPI QR encodes upi://pay?pa=…, so when nobody typed a UPI id we decode
//      the picture and recover one (see utils/qrDecode.js) — without that a payee who only uploaded
//      an image left payers with nothing to tap but "save the photo", and they paid offline. The
//      recovered id also lets the link carry the amount, which a static collect QR never does.
//      Where the tap goes is an OS question, not a UPI one: Android hands upi:// to its own app
//      chooser, so the tap fires the link directly; iOS has no chooser and the scheme lands on
//      whichever app claimed it (often WhatsApp Pay, which can't settle an arbitrary VPA), so an
//      iPhone gets our list of named apps instead. Desktop gets the list too — a upi:// click there
//      does nothing.
//   3. Copy the VPA and pay manually. Works on any app, on any phone, always.
import React, { useEffect, useState } from 'react';
import QrCode from './QrCode';
import { buildUpiUri, buildUpiAppLinks, parseUpiString, isLikelyVpa } from '../utils/upi';
import { decodeQrFromImageSrc } from '../utils/qrDecode';
import { isIOS } from '../utils/deviceContacts';
import { useQuery } from '@apollo/client';
import { IS_FEATURE_ENABLED_QUERY } from '../graphql';

const PREFERRED_APP_KEY = 'hc_upi_app';
// Tap-to-open and the per-app links are gated because they don't work end to end: the app opens,
// then the PSP declines a person-to-person payment started from a link. Fails CLOSED — only an
// explicit true turns them on — so a slow or unreachable API shows the route that works (scanning)
// rather than the one that ends in a refusal.
const DEEPLINK_FLAG = 'upi_deeplink_enabled';

export default function UpiPayPanel({
  vpa,
  payeeName,
  amountPaise,
  note,
  upiUri: providedUri,     // the server builds this for group join fees; recomputed if absent
  qrImage,                 // the payee's own QR, uploaded from their UPI app — preferred when present
  qrSize = 180,
  children,                // the confirm action ("I've paid", "Place order", …)
}) {
  const [copied, setCopied] = useState('');
  const [showApps, setShowApps] = useState(false);
  const [derivedVpa, setDerivedVpa] = useState('');
  // Which app this person actually pays with. iOS can't be asked ("which apps are installed?" is not
  // answerable from a browser), so we ask once by letting them pick, then remember — the second
  // payment onward is a single tap straight into their app.
  const [preferredApp, setPreferredApp] = useState(() => {
    try { return localStorage.getItem(PREFERRED_APP_KEY) || ''; } catch { return ''; }
  });
  const rememberApp = (id) => {
    setPreferredApp(id);
    try { localStorage.setItem(PREFERRED_APP_KEY, id); } catch { /* private mode — one tap, not none */ }
  };

  // Recover the payee address from the picture when nobody typed one — this is what rescues QRs
  // uploaded before the id was read at upload time, and it is why tapping can open an app at all.
  // Read-only: a payer's browser must never write a decoded VPA back to the server.
  useEffect(() => {
    let cancelled = false;
    if (!qrImage || vpa) { setDerivedVpa(''); return undefined; }
    decodeQrFromImageSrc(qrImage).then(text => {
      const parsed = text ? parseUpiString(text) : null;
      if (!cancelled && parsed?.vpa && isLikelyVpa(parsed.vpa)) setDerivedVpa(parsed.vpa);
    });
    return () => { cancelled = true; };
  }, [qrImage, vpa]);

  // Everything below asks "can we pay by link?", which is true for a typed id or a decoded one.
  const payVpa = vpa || derivedVpa;
  const uri = payVpa ? (vpa ? (providedUri || buildUpiUri({ vpa, name: payeeName, amountPaise, note }))
                           : buildUpiUri({ vpa: payVpa, name: payeeName, amountPaise, note })) : '';
  const onIOS = isIOS();
  const { data: deeplinkFlag } = useQuery(IS_FEATURE_ENABLED_QUERY, {
    variables: { key: DEEPLINK_FLAG }, fetchPolicy: 'cache-first', errorPolicy: 'ignore',
  });
  const deeplinksOn = deeplinkFlag?.isFeatureEnabled === true;
  const appLinks = payVpa && deeplinksOn ? buildUpiAppLinks({ vpa: payVpa, name: payeeName, amountPaise, note }, { ios: onIOS }) : [];
  // 'any' is the generic upi:// link — never a *remembered* choice, since remembering it would be
  // remembering "let the OS guess", which is the thing this panel exists to avoid.
  const preferredLink = preferredApp && preferredApp !== 'any'
    ? appLinks.find(a => a.id === preferredApp) : null;
  const rupees = (amountPaise / 100).toFixed(2);
  const ownQr = !!qrImage;
  // Android hands a upi:// link to its own app chooser, which is exactly the "open my UPI app" the
  // tap promises. iOS has no chooser — the scheme goes to whichever app claimed it, often WhatsApp
  // Pay, which can't settle an arbitrary VPA — so an iPhone gets our named list instead. Desktop
  // gets the list too, where a upi:// click would simply do nothing.
  const directOpen = deeplinksOn && !!uri && /Android/i.test(typeof navigator === 'undefined' ? '' : navigator.userAgent) && !onIOS;
  // On iOS the tap can still be one tap — just into the app they already told us they use.
  const tapHref = !deeplinksOn ? '' : (directOpen ? uri : (onIOS && preferredLink ? preferredLink.href : ''));

  const copy = (text, what) => {
    navigator.clipboard?.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(''), 1800);
  };

  // The code, and the wrapper that makes it tappable without looking like a button.
  const qrNode = ownQr
    ? <img src={qrImage} alt={`${payeeName || 'Payee'}'s UPI QR code`}
           style={{ width: qrSize, height: qrSize, objectFit: 'contain', borderRadius: 8, background: '#fff', padding: 6, display: 'block' }} />
    : <QrCode data={uri} size={qrSize} alt={`UPI QR for ₹${rupees}`} />;
  const tapTargetStyle = {
    display: 'inline-block', padding: 4, border: '2px solid transparent', borderRadius: 12,
    background: 'none', cursor: 'pointer', lineHeight: 0, WebkitTapHighlightColor: 'transparent',
  };
  // Take the extension from the data URL rather than assuming one: the uploader always re-encodes as
  // JPEG (utils/imageUpload.js), so naming the saved file .png hands the gallery a lying extension —
  // and the gallery is where this file is going, to be re-scanned by a UPI app.
  const qrExt = (/^data:image\/([a-z]+)/i.exec(qrImage || '')?.[1] || 'png').toLowerCase().replace('jpeg', 'jpg');

  if (!uri && !ownQr) {
    return (
      <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(220,38,38,0.08)', color: 'var(--danger)', fontSize: 'var(--font-sm)' }}>
        No UPI id is set up for this payment yet — ask the organiser.
      </div>
    );
  }

  return (
    <div className="card" style={{ background: 'var(--surface2)', textAlign: 'center' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Pay ₹{rupees} to</div>
      {payVpa && <div style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--primary)', wordBreak: 'break-all' }}>{payVpa}</div>}
      {payeeName && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>{payeeName}</div>}

      {/* 1 — the reliable route. The code is also the tap target, because on a phone that is the
          first thing people try and a dead image is a dead end. Tapping opens the app chooser rather
          than firing a bare upi:// link: the OS hands that link to whichever app claimed the scheme,
          often WhatsApp Pay, which can't settle an arbitrary VPA — see utils/upi.js. With only an
          uploaded QR there is no VPA to build any link from, so the tap saves the image instead,
          which is exactly what the single-phone gallery-scan flow below needs. */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        {tapHref ? (
          // Somewhere definite to go: Android's own app chooser, or on iOS the app this person
          // already told us they use.
          <a href={tapHref} style={tapTargetStyle} aria-label={`Pay ₹${rupees} in your UPI app`}>
            {qrNode}
          </a>
        ) : uri && deeplinksOn ? (
          <button type="button" style={tapTargetStyle} onClick={() => setShowApps(s => !s)}
            aria-expanded={showApps} aria-label={`Choose a UPI app to pay ₹${rupees}`}>
            {qrNode}
          </button>
        ) : uri ? (
          // Deep links off: a plain, non-interactive code. Scanning it is the route that works.
          <div style={{ ...tapTargetStyle, cursor: 'default' }}>{qrNode}</div>
        ) : (
          // Nothing decoded and nothing typed — saving the picture is genuinely all that's left.
          <a href={qrImage} download={`upi-qr.${qrExt}`} style={tapTargetStyle}
            aria-label="Save this UPI QR code to your phone">
            {qrNode}
          </a>
        )}
      </div>
      <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', margin: '8px 0 2px' }}>
        {!uri ? '👆 Tap the code to save it to your phone'
          : !deeplinksOn ? '📷 Scan this code from inside your UPI app'
          : preferredLink && onIOS ? `👆 Tap the code to pay in ${preferredLink.label}`
          : '👆 Tap the code to pay in your UPI app'}
      </p>
      {onIOS && deeplinksOn && preferredLink && (
        <button type="button" onClick={() => { rememberApp(''); setShowApps(true); }}
          style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 4px', fontSize: 11, color: 'var(--text-muted)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>
          Not {preferredLink.label}? Pick a different app
        </button>
      )}
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', margin: '2px 0' }}>
        …or open your UPI app → Scan &amp; Pay → point it at this code
      </p>
      {ownQr && !uri && (
        // A raw collect QR carries no amount. Once we have a payee address the link does carry one,
        // so this warning would be wrong — hence it only shows on the no-link path.
        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--warning)', margin: '0 0 2px' }}>
          Enter the amount yourself: ₹{rupees}
        </p>
      )}
      <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>
        Scan from inside the app, not with your phone camera — the camera may hand it to WhatsApp.
      </p>
      {/* The single-device case: you can't point a phone at its own screen. Every major UPI app can
          read a QR out of the photo library, which is the way through — and it's not discoverable
          unless we say so. */}
      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        <strong>Paying on this same phone?</strong> {uri ? 'Screenshot this code' : 'Tap the code to save it'},
        then in your UPI app choose Scan &amp; Pay → <em>gallery / upload</em> and pick it.
        {payVpa && ' Or copy the UPI id below and pay manually.'}
      </p>

      {note && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>{note}</p>}

      {/* 2 — let the user choose the app, rather than the OS choosing for them. Nothing to offer
          when the payee gave us only a QR: an app link needs a VPA to send money to. */}
      {!payVpa || !deeplinksOn ? null : !showApps ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowApps(true)} style={{ marginBottom: 10 }}>
          📲 Choose a UPI app
        </button>
      ) : (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Pick the app you actually use:</div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            {appLinks.map(a => (
              <a key={a.id} className="btn btn-ghost btn-sm" href={a.href}
                onClick={() => rememberApp(a.id)}>{a.label}</a>
            ))}
          </div>
          {onIOS && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              We'll remember which one you pick and take you straight there next time.
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
            Nothing happens? That app isn't installed — use the QR or copy the UPI id.
          </div>
          {/* Not a bug we can fix: PSP apps routinely refuse person-to-person payments started from
              an external link ("declined for security reasons"), and tell the user to scan or type the
              id instead. Say so up front rather than letting it look like a broken payment. */}
          <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 6, lineHeight: 1.5 }}>
            If your app says <em>“declined for security reasons”</em>, that's its rule about payments
            opened from a link — not a problem with this one. Scan the QR above, or copy the UPI id
            and pay from inside the app.
          </div>
        </div>
      )}

      {/* 3 — always works */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {payVpa && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(payVpa, 'vpa')}>
            {copied === 'vpa' ? '✅ UPI id copied' : '📋 Copy UPI id'}
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(rupees, 'amt')}>
          {copied === 'amt' ? '✅ Amount copied' : `📋 Copy ₹${rupees}`}
        </button>
      </div>

      {children}
    </div>
  );
}
